import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./MonthlySaleReportPage.css";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  monthlySaleReportQuery,
  readMonthlySaleReportParams,
} from "../utils/monthlySaleReportNav";
import {
  fetchMonthlyAutosReport,
  mapMonthlyAutosReportToSalePageData,
} from "../services/monthlyAutosReportService";
import {
  loadLatestTopSales,
  loadMonthlyManualEntries,
  mergeCurrentMonthOverridesIntoSalePageData,
  mergeMonthlyManualIntoReport,
  mergePreviousMonthManualIntoSalePageData,
} from "../services/monthlySaleReportManualService";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
} from "../utils/financialYear";

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
  { value: "Wholesale", label: "Wholesale" },
];

function pctFixed(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(2);
}

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  const s = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
  return n < 0 ? `(${s})` : s;
}

function trendClass(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "";
  return Number(pct) >= 0 ? "msr-pos" : "msr-neg";
}

function pctText(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "-";
  return `${Number(pct) >= 0 ? "▲" : "▼"} ${Math.abs(Number(pct)).toFixed(2)}%`;
}

export default function MonthlySaleReportPage({ importOnly = false, hideUpload = false } = {}) {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readMonthlySaleReportParams(searchParams, now);
  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const fyMonthSequence = useMemo(() => getFinancialYearMonthSequence(fyStartYear), [fyStartYear]);

  useEffect(() => {
    setSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, branchFilter), { replace: true });
  }, [fyStartYear, fySlot, branchFilter, setSearchParams]);

  const uploadHref = `/reports/monthly-sale-report/upload?${monthlySaleReportQuery(
    fyStartYear,
    fySlot,
    branchFilter
  )}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (importOnly) {
          const report = await fetchMonthlyAutosReport(year, monthIndex, branchFilter, {
            importOnly: true,
            dateScope: "full",
          });
          if (!cancelled) setData(mapMonthlyAutosReportToSalePageData(report));
          return;
        } else {
          const prevPeriod = new Date(year, monthIndex - 1, 1);
          const [report, manualBundle, prevManualBundle, fixedTopSales] = await Promise.all([
            fetchMonthlyAutosReport(year, monthIndex, branchFilter),
            loadMonthlyManualEntries(year, monthIndex, branchFilter),
            loadMonthlyManualEntries(prevPeriod.getFullYear(), prevPeriod.getMonth(), branchFilter),
            loadLatestTopSales(year, monthIndex, 120, branchFilter),
          ]);
          const merged = mergeMonthlyManualIntoReport(report, manualBundle, branchFilter);
          const mapped = mapMonthlyAutosReportToSalePageData(merged);
          const mappedWithPrevManual = mergePreviousMonthManualIntoSalePageData(
            mapped,
            prevManualBundle,
            branchFilter
          );
          const mappedWithOverrides = mergeCurrentMonthOverridesIntoSalePageData(
            mappedWithPrevManual,
            manualBundle,
            prevManualBundle,
            fixedTopSales,
            branchFilter
          );
          if (!cancelled) setData(mappedWithOverrides);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter, importOnly]);

  const hero = data?.brands?.find((b) => b.n === "HERO");
  const honda = data?.brands?.find((b) => b.n === "HONDA");
  const bajaj = data?.brands?.find((b) => b.n === "BAJAJ");
  const bullet = data?.brands?.find((b) => b.n === "BULLET");
  const tvs = data?.brands?.find((b) => b.n === "TVS");
  const suzuki = data?.brands?.find((b) => b.n === "SUZUKI");
  const yamaha = data?.brands?.find((b) => b.n === "YAMAHA");
  const mahindra = data?.brands?.find((b) => b.n === "MAHINDRA");
  const piaggio = data?.brands?.find((b) => b.n === "PIAGGIO");
  const oeBrands = (data?.brands || []).filter((b) => b.oe != null && b.n !== "PIAGGIO");
  const brandsRowOrder = (data?.brands || []).filter((b) => b.n !== "SUZUKI");
  const showMiscAfterPiaggio = Boolean(piaggio && tvs && suzuki);
  const isBranch3 = branchFilter === "Branch 3";

  return (
    <>
      <div className="msr-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <div className="msr-layout">
        <section className="msr-report-col">
          {!data && !loading ? (
            <p className="msr-no-print msr-empty-msg">No report data loaded.</p>
          ) : null}
          {data ? (
          <table className="msr-table">
          <colgroup>
            <col style={{ width: "19%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "21%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <tbody>
            <tr>
              <th className="msr-title" colSpan={6}>{data.title}</th>
              <th className="msr-title msr-num">{data.corner}</th>
            </tr>
            <tr>
              <th className="msr-head" colSpan={2}>MONTHLY ANALYSIS SALE</th>
              <th className="msr-head" colSpan={2}></th>
              <th className="msr-head">AMOUNT</th>
              <th className="msr-head">MONTHLY ANALYSIS COLLECTION</th>
              <th className="msr-head">AMOUNT</th>
            </tr>
            <tr className="msr-analysis-gap-row">
              <td colSpan={7}></td>
            </tr>

            <tr>
              <td className="msr-lbl" colSpan={2}>TOTAL CASH SALE</td>
              <td className="msr-num" colSpan={3}>{fmt(data.cashSale)}</td>
              <td className="msr-lbl">CASH SALE</td>
              <td className="msr-num">{fmt(data.cashSale)}</td>
            </tr>
            <tr>
              <td className="msr-lbl" colSpan={2}>TOTAL CREDIT BILL</td>
              <td className="msr-num" colSpan={3}>{fmt(data.creditBill)}</td>
              <td className="msr-lbl">SALE COLLECTION</td>
              <td className="msr-num">{fmt(data.saleCollection)}</td>
            </tr>
            <tr>
              <td className="msr-lbl" colSpan={2}>TOTAL OTHER SALE</td>
              <td className="msr-num" colSpan={3}>{fmt(data.otherSale)}</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td colSpan={5}></td>
              <td></td>
              <td className="msr-num msr-hi msr-thick-top">{fmt(data.subtotal)}</td>
            </tr>

            <tr>
              <td className="msr-head"></td>
              <td className="msr-head"></td>
              <td className="msr-head msr-head-titlecase">Previous Month</td>
              <td className="msr-head msr-head-titlecase">Current Month</td>
              <td className="msr-head"></td>
              <td className="msr-head msr-lbl">LESS CASH DISCOUNT</td>
              <td className="msr-head">-</td>
            </tr>
            <tr>
              <td className="msr-lbl">Avg. Day Sale</td>
              <td></td>
              <td className="msr-num">{fmt(data.avgPrev)}</td>
              <td className="msr-num">{fmt(data.avgCurr)}</td>
              <td className={`msr-num msr-pct ${trendClass(data.avgPct)}`}>{pctText(data.avgPct)}</td>
              <td className="msr-lbl">LESS SALE RETURN</td>
              <td className="msr-num">{fmt(-Math.abs(data.lessSaleReturn))}</td>
            </tr>
            <tr>
              <td className="msr-lbl" colSpan={2}>TOTAL SALE</td>
              <td className="msr-num msr-hi" colSpan={3}>{fmt(data.totalSale)}</td>
              <td className="msr-lbl">TOTAL CASH IN HAND</td>
              <td className="msr-num msr-hi">{fmt(data.totalCashInHand)}</td>
            </tr>
            <tr>
              <td className="msr-lbl msr-center" colSpan={2}>TOTAL OE SALE</td>
              <td className="msr-lbl" colSpan={3}>TOTAL ORDINARY SALE</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td className="msr-num" colSpan={2}>{data.oePct}%</td>
              <td className="msr-num" colSpan={3}>{data.ordinaryPct}%</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td className="msr-num msr-neg" colSpan={2}>{fmt(data.oeAmt)}</td>
              <td className="msr-num msr-neg" colSpan={3}>{fmt(data.ordinaryAmt)}</td>
              <td></td>
              <td></td>
            </tr>

            <tr>
              <th className="msr-head"></th>
              <th className="msr-head msr-head-titlecase">Top Sale</th>
              <th className="msr-head msr-head-titlecase">Previous Month</th>
              <th className="msr-head msr-head-titlecase">Current Month</th>
              <th className="msr-head"></th>
              <th className="msr-head" colSpan={2}>SALES RETURN</th>
            </tr>

            {brandsRowOrder.map((b) => (
          <React.Fragment key={b.n}>
            {b.n !== "YAMAHA" && b.n !== "MAHINDRA" && b.n !== "PIAGGIO" ? (
              <>
            <tr>
              <td className="msr-lbl">{b.n} SALE</td>
              <td className="msr-num msr-top">{fmt(b.top)}</td>
              <td className="msr-num">{fmt(b.p)}</td>
              <td className="msr-num">{fmt(b.c)}</td>
              <td className={`msr-num msr-pct ${trendClass(b.pct)}`}>{pctText(b.pct)}</td>
              {b.n === "BULLET" ? (
                <td className="msr-head" colSpan={2} rowSpan={2}>OE &amp; ORDINARY SALE</td>
              ) : (
                <>
                  <td className="msr-lbl msr-sr-lbl">
                    {b.n === "BAJAJ" && bullet
                      ? bullet.rLabel
                      : b.n === "TVS" && hero
                        ? (
                            <>
                              {hero.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(hero.oe)}%)</span>
                            </>
                          )
                        : b.n === "TVS" || b.n === "HONDA"
                          ? ""
                          : (b.rLabel || "")}
                  </td>
                  <td className="msr-num msr-sr-num">
                    {b.n === "BAJAJ" && bullet
                      ? fmt(bullet.rAmt)
                      : b.n === "TVS" && hero
                        ? fmt(hero.oeAmt)
                        : b.n === "TVS" || b.n === "HONDA"
                          ? ""
                          : (b.rLabel ? fmt(b.rAmt) : "")}
                  </td>
                </>
              )}
            </tr>
            <tr className={b.n === "HONDA" ? "msr-honda-purchase-row" : (b.n === "BAJAJ" ? "msr-bajaj-purchase-row" : (b.n === "BULLET" ? "msr-bullet-purchase-row" : ""))}>
              <td className="msr-lbl">{b.n} PURCHASE</td>
              <td></td>
              <td className="msr-num">{fmt(b.pp)}</td>
              <td className="msr-num">{fmt(b.pc)}</td>
              <td></td>
              {b.n === "BULLET" ? null : (
                <>
                  <td className="msr-lbl msr-sr-lbl">
                    {b.n === "HONDA" && bajaj
                      ? bajaj.rLabel
                      : b.n === "TVS" && hero
                        ? (
                            <>
                              {hero.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(hero.oth)}%)</span>
                            </>
                          )
                        : ""}
                  </td>
                  <td className="msr-num msr-sr-num">
                    {b.n === "HONDA" && bajaj
                      ? fmt(bajaj.rAmt)
                      : b.n === "TVS" && hero
                        ? fmt(hero.othAmt)
                        : ""}
                  </td>
                </>
              )}
            </tr>
              </>
            ) : null}
            {b.n === "TVS" && hero ? (
              <tr className="msr-hero-oe-total-row msr-tvs-after-purchase-total-row">
                <td colSpan={5}></td>
                <td className="msr-lbl msr-hi"></td>
                <td className="msr-num msr-hi">{fmt(hero.c)}</td>
              </tr>
            ) : null}
            {b.n === "BULLET" ? (
              <tr className="msr-bullet-gap-row">
                <td></td><td></td><td></td><td></td><td></td><td></td><td></td>
              </tr>
            ) : null}
            {b.n === "TVS" && suzuki ? (
              <>
                <tr>
                  <td className="msr-lbl">{suzuki.n} SALE</td>
                  <td className="msr-num msr-top">{fmt(suzuki.top)}</td>
                  <td className="msr-num">{fmt(suzuki.p)}</td>
                  <td className="msr-num">{fmt(suzuki.c)}</td>
                  <td className={`msr-num msr-pct ${trendClass(suzuki.pct)}`}>{pctText(suzuki.pct)}</td>
                  <td className="msr-lbl msr-sr-lbl">
                    {honda ? (
                      <>
                        {honda.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(honda.oe)}%)</span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="msr-num msr-sr-num">{honda ? fmt(honda.oeAmt) : ""}</td>
                </tr>
                <tr>
                  <td className="msr-lbl">{suzuki.n} PURCHASE</td>
                  <td></td>
                  <td className="msr-num">{fmt(suzuki.pp)}</td>
                  <td className="msr-num">{fmt(suzuki.pc)}</td>
                  <td></td>
                  <td className="msr-lbl msr-sr-lbl">
                    {honda ? (
                      <>
                        {honda.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(honda.oth)}%)</span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="msr-num msr-sr-num">{honda ? fmt(honda.othAmt) : ""}</td>
                </tr>
                {honda ? (
                  <tr className="msr-hero-oe-total-row">
                    <td colSpan={5}></td>
                    <td className="msr-lbl msr-hi"></td>
                    <td className="msr-num msr-hi">{fmt(honda.c)}</td>
                  </tr>
                ) : null}
                {yamaha && bajaj ? (
                  <>
                    <tr>
                      <td className="msr-lbl">{yamaha.n} SALE</td>
                      <td className="msr-num msr-top">{fmt(yamaha.top)}</td>
                      <td className="msr-num">{fmt(yamaha.p)}</td>
                      <td className="msr-num">{fmt(yamaha.c)}</td>
                      <td className={`msr-num msr-pct ${trendClass(yamaha.pct)}`}>{pctText(yamaha.pct)}</td>
                      <td className="msr-lbl msr-sr-lbl">
                        {bajaj.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(bajaj.oe)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(bajaj.oeAmt)}</td>
                    </tr>
                    <tr>
                      <td className="msr-lbl">{yamaha.n} PURCHASE</td>
                      <td></td>
                      <td className="msr-num">{fmt(yamaha.pp)}</td>
                      <td className="msr-num">{fmt(yamaha.pc)}</td>
                      <td></td>
                      <td className="msr-lbl msr-sr-lbl">
                        {bajaj.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(bajaj.oth)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(bajaj.othAmt)}</td>
                    </tr>
                    <tr className="msr-hero-oe-total-row">
                      <td colSpan={5}></td>
                      <td className="msr-lbl msr-hi"></td>
                      <td className="msr-num msr-hi">{fmt(bajaj.c)}</td>
                    </tr>
                  </>
                ) : null}
                {mahindra && bullet ? (
                  <>
                    <tr>
                      <td className="msr-lbl">{mahindra.n} SALE</td>
                      <td className="msr-num msr-top">{fmt(mahindra.top)}</td>
                      <td className="msr-num">{fmt(mahindra.p)}</td>
                      <td className="msr-num">{fmt(mahindra.c)}</td>
                      <td className={`msr-num msr-pct ${trendClass(mahindra.pct)}`}>{pctText(mahindra.pct)}</td>
                      <td className="msr-lbl msr-sr-lbl">
                        {bullet.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(bullet.oe)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(bullet.oeAmt)}</td>
                    </tr>
                    <tr>
                      <td className="msr-lbl">{mahindra.n} PURCHASE</td>
                      <td></td>
                      <td className="msr-num">{fmt(mahindra.pp)}</td>
                      <td className="msr-num">{fmt(mahindra.pc)}</td>
                      <td></td>
                      <td className="msr-lbl msr-sr-lbl">
                        {bullet.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(bullet.oth)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(bullet.othAmt)}</td>
                    </tr>
                    <tr className="msr-hero-oe-total-row">
                      <td colSpan={5}></td>
                      <td className="msr-lbl msr-hi"></td>
                      <td className="msr-num msr-hi">{fmt(bullet.c)}</td>
                    </tr>
                  </>
                ) : null}
                {piaggio && tvs ? (
                  <>
                    <tr>
                      <td className="msr-lbl">{piaggio.n} SALE</td>
                      <td className="msr-num msr-top">{fmt(piaggio.top)}</td>
                      <td className="msr-num">{fmt(piaggio.p)}</td>
                      <td className="msr-num">{fmt(piaggio.c)}</td>
                      <td className={`msr-num msr-pct ${trendClass(piaggio.pct)}`}>{pctText(piaggio.pct)}</td>
                      <td className="msr-lbl msr-sr-lbl">
                        {tvs.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(tvs.oe)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(tvs.oeAmt)}</td>
                    </tr>
                    <tr>
                      <td className="msr-lbl">{piaggio.n} PURCHASE</td>
                      <td></td>
                      <td className="msr-num">{fmt(piaggio.pp)}</td>
                      <td className="msr-num">{fmt(piaggio.pc)}</td>
                      <td></td>
                      <td className="msr-lbl msr-sr-lbl">
                        {tvs.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(tvs.oth)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(tvs.othAmt)}</td>
                    </tr>
                    <tr className="msr-hero-oe-total-row">
                      <td colSpan={5}></td>
                      <td className="msr-lbl msr-hi"></td>
                      <td className="msr-num msr-hi">{fmt(tvs.c)}</td>
                    </tr>
                    <tr className="msr-misc-section-start">
                      <td className="msr-lbl" colSpan={2}>MISCELLANEOUS ITEM SALES</td>
                      <td className="msr-num msr-hi" colSpan={3}>{fmt(data.miscSale)}</td>
                      <td className="msr-lbl msr-sr-lbl">
                        {suzuki.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(suzuki.oe)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(suzuki.oeAmt)}</td>
                    </tr>
                    <tr>
                      <td className="msr-lbl" colSpan={2}>MISCELLANEOUS ITEM PURCHASE</td>
                      <td className="msr-num msr-hi" colSpan={3}>{fmt(data.miscPurchase)}</td>
                      <td className="msr-lbl msr-sr-lbl">
                        {suzuki.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(suzuki.oth)}%)</span>
                      </td>
                      <td className="msr-num msr-sr-num">{fmt(suzuki.othAmt)}</td>
                    </tr>
                    <tr className="msr-hero-oe-total-row">
                      <td colSpan={5}></td>
                      <td className="msr-lbl msr-hi"></td>
                      <td className="msr-num msr-hi">{fmt(suzuki.c)}</td>
                    </tr>
                    {yamaha ? (
                      <>
                        <tr>
                          <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER OUT NEW</td>
                          <td className="msr-num">{fmt(data.transferOutPrev)}</td>
                          <td className="msr-num">{fmt(data.transferOutCurr)}</td>
                          <td></td>
                          <td className="msr-lbl msr-sr-lbl">
                            {yamaha.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(yamaha.oe)}%)</span>
                          </td>
                          <td className="msr-num msr-sr-num">{fmt(yamaha.oeAmt)}</td>
                        </tr>
                        <tr>
                          <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER IN</td>
                          <td className="msr-num">-</td>
                          <td className="msr-num">-</td>
                          <td></td>
                          <td className="msr-lbl msr-sr-lbl">
                            {yamaha.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(yamaha.oth)}%)</span>
                          </td>
                          <td className="msr-num msr-sr-num">{fmt(yamaha.othAmt)}</td>
                        </tr>
                        <tr className="msr-hero-oe-total-row">
                          <td colSpan={5}></td>
                          <td className="msr-lbl msr-hi"></td>
                          <td className="msr-num msr-hi">{fmt(yamaha.c)}</td>
                        </tr>
                      </>
                    ) : (
                      <>
                        <tr>
                          <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER OUT NEW</td>
                          <td className="msr-num">{fmt(data.transferOutPrev)}</td>
                          <td className="msr-num">{fmt(data.transferOutCurr)}</td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                        <tr>
                          <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER IN</td>
                          <td className="msr-num">-</td>
                          <td className="msr-num">-</td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      </>
                    )}
                  </>
                ) : null}
              </>
            ) : null}
            {b.n === "HONDA" ? (
              <tr className="msr-honda-gap-row">
                <td></td><td></td><td></td><td></td><td></td><td></td><td></td>
              </tr>
            ) : null}
            {b.n === "BAJAJ" ? (
              <tr className="msr-bajaj-gap-row">
                <td></td><td></td><td></td><td></td><td></td>
                <td className="msr-lbl msr-sr-lbl">{tvs ? tvs.rLabel : ""}</td>
                <td className="msr-num msr-sr-num">{tvs ? fmt(tvs.rAmt) : ""}</td>
                
              </tr>
            ) : null}
            {b.n === "HERO" && honda ? (
              <tr>
                <td></td><td></td><td></td><td></td><td></td>
                <td className="msr-lbl msr-sr-lbl">{honda.rLabel}</td>
                <td className="msr-num msr-sr-num">{fmt(honda.rAmt)}</td>
                
              </tr>
            ) : null}
            {b.n === "TVS"
              ? oeBrands.filter((ob) => ob.n !== "HERO" && ob.n !== "HONDA" && ob.n !== "BAJAJ" && ob.n !== "BULLET" && ob.n !== "TVS" && ob.n !== "SUZUKI" && ob.n !== "YAMAHA").map((ob) => (
                  <React.Fragment key={`${ob.n}-oe-block`}>
                    <tr>
                      <td></td><td></td><td></td><td></td><td></td>
                      <td className="msr-lbl">{ob.n} OE <span className="msr-neg">({pctFixed(ob.oe)}%)</span></td>
                      <td className="msr-num">{fmt(ob.oeAmt)}</td>
                    </tr>
                    <tr>
                      <td></td><td></td><td></td><td></td><td></td>
                      <td className="msr-lbl">{ob.n} OTHER <span className="msr-neg">({pctFixed(ob.oth)}%)</span></td>
                      <td className="msr-num">{fmt(ob.othAmt)}</td>
                    </tr>
                    <tr>
                      <td></td><td></td><td></td><td></td><td></td>
                      <td className="msr-lbl msr-hi"></td>
                      <td className="msr-num msr-hi">{fmt(ob.c)}</td>
                    </tr>
                  </React.Fragment>
                ))
              : null}
          </React.Fragment>
        ))}

        {!showMiscAfterPiaggio ? (
          <>
            <tr className="msr-misc-section-start">
              <td className="msr-lbl" colSpan={2}>MISCELLANEOUS ITEM SALES</td>
              <td className="msr-num msr-hi" colSpan={3}>{fmt(data.miscSale)}</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td className="msr-lbl" colSpan={2}>MISCELLANEOUS ITEM PURCHASE</td>
              <td className="msr-num msr-hi" colSpan={3}>{fmt(data.miscPurchase)}</td>
              <td></td>
              <td></td>
            </tr>
            {yamaha ? (
              <>
                <tr>
                  <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER OUT NEW</td>
                  <td className="msr-num">{fmt(data.transferOutPrev)}</td>
                  <td className="msr-num">{fmt(data.transferOutCurr)}</td>
                  <td></td>
                  <td className="msr-lbl msr-sr-lbl">
                    {yamaha.n} OE <span className="msr-neg msr-inline-pct">({pctFixed(yamaha.oe)}%)</span>
                  </td>
                  <td className="msr-num msr-sr-num">{fmt(yamaha.oeAmt)}</td>
                </tr>
                <tr>
                  <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER IN</td>
                  <td className="msr-num">-</td>
                  <td className="msr-num">{isBranch3 ? fmt(data.transferIn) : "-"}</td>
                  <td></td>
                  <td className="msr-lbl msr-sr-lbl">
                    {yamaha.n} OTHER <span className="msr-neg msr-inline-pct">({pctFixed(yamaha.oth)}%)</span>
                  </td>
                  <td className="msr-num msr-sr-num">{fmt(yamaha.othAmt)}</td>
                </tr>
                <tr className="msr-hero-oe-total-row">
                  <td colSpan={5}></td>
                  <td className="msr-lbl msr-hi"></td>
                  <td className="msr-num msr-hi">{fmt(yamaha.c)}</td>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER OUT NEW</td>
                  <td className="msr-num">{fmt(data.transferOutPrev)}</td>
                  <td className="msr-num">{fmt(data.transferOutCurr)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td className="msr-lbl" colSpan={2}>BRANCH TRANSFER IN</td>
                  <td className="msr-num">-</td>
                  <td className="msr-num">{isBranch3 ? fmt(data.transferIn) : "-"}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            )}
          </>
        ) : null}
        <tr>
          <td></td><td></td><td></td><td></td><td></td><td></td><td></td>
        </tr>
        <tr className="msr-footer-total-row">
          <td className="msr-lbl" colSpan={2}>TOTAL SALE</td>
          <td className="msr-num msr-hi msr-total-sale" colSpan={3}>{fmt(data.totalSale)}</td>
          <td></td>
          <td></td>
        </tr>
        <tr className="msr-footer-total-row">
          <td className="msr-lbl" colSpan={2}>TOTAL PURCHASE</td>
          <td className="msr-num msr-total-purchase" colSpan={3}>{fmt(data.totalPurchase)}</td>
          <td></td>
          <td></td>
        </tr>
        {/* Indirect expense upload is stored in Firestore only — not listed on this report */}
      </tbody>
      </table>
      ) : null}
        </section>

        <aside className="msr-sidepanel msr-no-print">
          <header className="msr-side-head">
            <p className="msr-side-kicker">Import report</p>
            <h2 className="msr-side-title">Period &amp; print</h2>
            <p className="msr-side-period">
              {MONTH_OPTIONS[monthIndex]} {year}
              <span>{fyLabel} · Apr–Mar</span>
            </p>
          </header>

          <div className="msr-side-fields">
            <label className="msr-side-field">
              <span>Financial year</span>
              <select
                value={fyStartYear}
                onChange={(e) => setFyStartYear(Number(e.target.value))}
                title="April–March (e.g. 2025-26)"
              >
                {fySelectOptions.map(({ startYear, label }) => (
                  <option key={startYear} value={startYear}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="msr-side-field">
              <span>Month</span>
              <select
                value={fySlot}
                onChange={(e) => setFySlot(Number(e.target.value))}
                title="April through March within the selected financial year"
              >
                {fyMonthSequence.map((s) => (
                  <option key={`${s.calendarYear}-${s.monthIndex}`} value={s.fySlot}>
                    {MONTH_OPTIONS[s.monthIndex]} {s.calendarYear}
                  </option>
                ))}
              </select>
            </label>
            <label className="msr-side-field">
              <span>Branch</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                {BRANCH_OPTIONS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {importOnly && branchFilter === "Main" ? (
            <details className="msr-side-help">
              <summary>Main branch — how figures are built</summary>
              <ul>
                <li>
                  Sales from <strong>Outward Supply</strong>, <strong>Credit Sales</strong>, Credit Notes.
                </li>
                <li>
                  BillAmount − line Amount gap → <strong>Other Sale</strong>.
                </li>
                <li>
                  <strong>Transfer Out - New</strong> (Branch ID 1) → Branch Transfer Out.
                </li>
                <li>
                  Purchase: <strong>Inward Supply</strong> (Branch ID 1) less Transfer Out - New to{" "}
                  <strong>WINS BRANCH (S.DEBTOR)</strong>.
                </li>
              </ul>
            </details>
          ) : null}

          {importOnly && branchFilter === "Wholesale" ? (
            <details className="msr-side-help">
              <summary>Wholesale — how figures are built</summary>
              <ul>
                <li>
                  Sales from import voucher type <strong>Credit Sales WS</strong>.
                </li>
                <li>
                  These lines count as <strong>Credit Bill</strong> on the report.
                </li>
              </ul>
            </details>
          ) : null}

          {loading ? <p className="msr-side-status">Loading report…</p> : null}
          {error ? <p className="msr-side-error">{error}</p> : null}

          <div className="msr-side-actions">
            {importOnly && !loading ? (
              <Link
                to={`/reports/monthly-sale-report-import/generate?${monthlySaleReportQuery(
                  fyStartYear,
                  fySlot,
                  branchFilter
                )}`}
                className="msr-side-btn msr-side-btn--primary"
              >
                Generate totals
              </Link>
            ) : null}
            {!hideUpload ? (
              <Link to={uploadHref} className="msr-side-btn">
                Upload data
              </Link>
            ) : null}
            <button type="button" className="msr-side-btn" onClick={() => window.print()}>
              Print A4
            </button>
          </div>
        </aside>
      </div>
    </>
  );
}
